/**
 * Add an inspection item to an offboarding request —
 * `POST offboarding/inspections/`. Manager roles only; the `offboarding` FK
 * comes from the route (`:offboardingId`), not a form field. Creating an item
 * re-runs reconciliation server-side automatically, unlike editing one.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { createInspectionItem } from '../../api/endpoints/offboarding';

const INITIAL_FORM = {
  name: '',
  condition: '',
  is_damaged: false,
  repair_cost: '',
  notes: '',
};

type FormState = typeof INITIAL_FORM;

export function InspectionItemFormPage(): React.JSX.Element {
  const { offboardingId } = useParams<{ offboardingId: string }>();
  const parentId = Number(offboardingId);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  function update<K extends keyof FormState>(field: K, value: FormState[K]): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () =>
      createInspectionItem({
        offboarding: parentId,
        name: form.name,
        condition: form.condition,
        is_damaged: form.is_damaged,
        repair_cost: form.repair_cost === '' ? undefined : form.repair_cost,
        notes: form.notes,
      }),
    onSuccess: () => navigate(`../../${parentId}`, { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (!Number.isFinite(parentId)) {
    return <p>Invalid offboarding request id.</p>;
  }

  return (
    <section>
      <h2>Add inspection item</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="inspection-name">Name</label>
        <input
          id="inspection-name"
          required
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
        />
        {fieldError('name') !== undefined && <p className="form__field-error">{fieldError('name')}</p>}

        <label htmlFor="inspection-condition">Condition</label>
        <input
          id="inspection-condition"
          value={form.condition}
          onChange={(event) => update('condition', event.target.value)}
        />

        <label htmlFor="inspection-damaged">
          <input
            id="inspection-damaged"
            type="checkbox"
            checked={form.is_damaged}
            onChange={(event) => update('is_damaged', event.target.checked)}
          />{' '}
          Damaged
        </label>

        <label htmlFor="inspection-repair-cost">Repair cost</label>
        <input
          id="inspection-repair-cost"
          value={form.repair_cost}
          onChange={(event) => update('repair_cost', event.target.value)}
        />

        <label htmlFor="inspection-notes">Notes</label>
        <textarea
          id="inspection-notes"
          value={form.notes}
          onChange={(event) => update('notes', event.target.value)}
        />

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Add item'}
        </button>
      </form>
    </section>
  );
}

export default InspectionItemFormPage;
